import { GDFMetarTafFlightCategory } from '../gdf/type';
import {
    BarbColorType,
    BarbSize,
    FaaDelayMinimum,
    HeatmapStyle,
    IataNullReportColor,
    IataTurbulenceDisplayType,
    IataTurbulencePalette,
    IataTurbValue,
    MetarDisplayMode,
    MinimumAshCloudConcentration,
    MinimumCeiling,
    MinimumContrailsLevel,
    MinimumDsua,
    MinimumIcing,
    MinimumPirepIcing,
    MinimumPirepTurbulence,
    MinimumTapsLevel,
    MinimumVisibility,
    ParticleColor,
    ProbSeverePrimaryDisplay,
    ProbSevereSecondaryDisplay,
    RadarSummaryArrowStyle,
    RvrMinimum,
    WeatherJetStreamWidth,
    WeatherLightningColor,
    WeatherLightningSymbol,
    WeatherSettings,
    WeatherTimelineRange,
    WeatherTimelineSyncMode,
    WeatherTimelineTimeMode,
    WindDepiction,
    WindTemp
} from './settings';

export const DEFAULT_WEATHER_SETTINGS: WeatherSettings = {
    rasterInterpolation: {
        paletteInterpolation: 'nearest',
        rasterInterpolation: 'nearest'
    },
    animation: {
        running: false,
        timeToShowSlideSeconds: 0.5,
        numberOfSlides: 16,
        pauseBetweenCyclesSeconds: 2,
        timelineSyncMode: WeatherTimelineSyncMode.auto,
        timelineSyncCustomProduct: null,
        timelineTimeRange: WeatherTimelineRange.auto,
        timelineTimeMode: WeatherTimelineTimeMode.relative
    },
    altitude: {
        value: 0
    },
    winds: {
        windDepiction: WindDepiction.barbs,
        heatmapStyle: HeatmapStyle.none,
        heatmapOpacity: 0.5,
        particleColor: ParticleColor.auto,
        particleOpacity: 0.2,
        barbColorType: BarbColorType.auto,
        barbColor: '#000',
        jetStreamWidth: WeatherJetStreamWidth.medium,
        windTemp: WindTemp.both,
        barbSize: BarbSize.medium,
        probSeverePrimaryDisplay: ProbSeverePrimaryDisplay.combined,
        probSeverePrimaryMinLevel: 0,
        probSevereSecondaryDisplay: ProbSevereSecondaryDisplay.hail,
        probSevereSecondaryMinLevel: 50,
        probSevereShowArrow: true,
        probSevereShowTrail: true
    },
    airport: {
        displayMode: MetarDisplayMode.category,
        showMetarDetailInColor: false,
        minimumCategory: GDFMetarTafFlightCategory.unknown,
        minimumCeiling: MinimumCeiling.unknown,
        minimumVisibility: MinimumVisibility.unknown,
        faaDelayMinimum: FaaDelayMinimum.noDelays,
        rvrMinimum: RvrMinimum.all,
        filterGroup: null,
        myFleetAirports: false
    },
    radar: {
        nexradMinLevel: 0,
        radarSmoothing: false,
        nexradRainfallMinLevel: 0,
        nexradEchoTopsMinLevel: 0,
        satelliteMinLevel: 0,
        radarSummaryShowSpeedLabel: true,
        radarSummarySpeedArrowStyle: RadarSummaryArrowStyle.speedBased,
        enableRadarTransparency: false,
        radarTransparency: 100,
        satelliteMinTemp: 30,
        enableSatelliteTransparency: false,
        satelliteTransparency: 100
    },
    turbulenceIcing: {
        minimumIataLevel: 0,
        minimumTapsLevel: MinimumTapsLevel.smooth,
        iataTurbValue: IataTurbValue.peak,
        iataDisplayNullReport: true,
        iataNullReportColor: IataNullReportColor.gray,
        iataDisplayType: IataTurbulenceDisplayType.edr,
        iataSeverityPalette: IataTurbulencePalette.industry,
        iataShowWindBarbs: false,
        iataMaxReportTime: 120,
        iataFadeOldReports: false,
        minimumEdrLevel: 2,
        minimumIcingLevel: MinimumIcing.trace,
        icingApplyTransparency: true,
        turbulenceApplyTransparency: true
    },
    airmetsSigmets: {
        showTurb: true,
        showIcing: true,
        showConvective: true,
        showVolcanicAsh: true,
        showBlowingDust: true,
        showVisibility: true,
        showSurfaceWind: true,
        showBlowingSand: true,
        showOzone: true,
        showIfr: true,
        showMountainObscuration: true,
        showMountainWave: true,
        showTropicalCyclone: true,
        showThunderstorm: true,
        showLlws: true,
        showFreezingLevel: true
    },
    lightning: {
        symbol: WeatherLightningSymbol.plus,
        color: WeatherLightningColor.faded
    },
    airspace: {
        showTurbulence: true,
        minimumTurbulence: MinimumPirepTurbulence.smooth,
        showIcing: true,
        minimumIcing: MinimumPirepIcing.negative,
        showWeather: true,
        showWindShear: true,
        minAltitude: null,
        maxAltitude: null,
        reportedLast: 120,
        aircraftSmall: true,
        aircraftLarge: true,
        aircraftHeavy: true,
        aircraftUnknown: true,
        showFea: false,
        showFca: false,
        showFcaFsm: true,
        showFcaAfp: true,
        tfrApplyTransparency: true
    },
    dsua: {
        show: false,
        alert: false,
        caution: false,
        danger: false,
        militaryArea: false,
        prohibited: false,
        restricted: false,
        training: false,
        warning: false,
        unknown: false,
        filterGroup: {
            show: false,
            ids: []
        },
        minimumLevel: MinimumDsua.notScheduled
    },
    misc: {
        minimumContrailsLevel: MinimumContrailsLevel.low,
        enablePolygonTransparency: true,
        polygonTransparency: 40,
        enhancedThunderStormMinLevel: 0,
        minimumAshCloudConcentration: MinimumAshCloudConcentration.low,
    },
    other: {
        autoArrangeLayers: true
    },
    sigWx: {
        labelsMinZoomLevel: 4
    }
} as const;
